import { useState } from "react";
import partnersData from "../partner-data/partners.json";
import PartnerCard from "../components/PartnerCard";
import { Helmet, HelmetProvider } from "react-helmet-async";

const Partners = () => {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(partnersData);

  const handleSearch = (inputText = searchText) => {
    const searchTextLower = inputText.toLowerCase();
    const countryFilteredData = selectedCountry
      ? partnersData.filter((region) => region.country === selectedCountry)
      : partnersData;

    const allPartners = countryFilteredData.flatMap((region) =>
      region.partners.map((partner) => ({
        ...partner,
        country: region.country,
      }))
    );

    const filteredPartners = allPartners.filter((partner) =>
      selectedCountry
        ? partner.name.toLowerCase().includes(searchTextLower)
        : partner.name.toLowerCase().includes(searchTextLower) ||
        partner.country.toLowerCase().includes(searchTextLower)
    );

    if (inputText.trim() === "" && !selectedCountry) {
      setFilteredData(partnersData);
      return;
    }

    const groupedByCountry = filteredPartners.reduce((res, partner) => {
      const country = partner.country;
      if (!res[country]) {
        res[country] = [];
      }
      res[country].push(partner);
      return res;
    }, {});

    const finalData = Object.entries(groupedByCountry).map(
      ([country, partners]) => ({
        country,
        partners,
      })
    );

    setFilteredData(finalData);
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setSearchText("");
    if (country) {
      const result = partnersData.filter((region) => region.country === country);
      setFilteredData(result);
    } else {
      setFilteredData(partnersData);
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Partners - Aronium</title>
          <meta name="description" content="Find local support from Aronium official partners" />
        </Helmet>
      </HelmetProvider>

      <section className="max-w-screen-xl mx-auto rounded divide-slate-200 px-4 mb-10">
        <section className="py-8">
          <div className="max-w-screen-xl mx-auto text-center">
            <h1 className="text-4xl mb-4">Partners</h1>
            <p>Find local support from our partners in your region</p>
          </div>
        </section>
        <div>
          <div className="sm:grid flex flex-col md:flex-row gap-4 gap-8 lg:gap-20 mb-8 items-center justify-center">
            <div className="md:flex items-center gap-4 w-full sm:w-auto">
              <select
                value={selectedCountry}
                onChange={handleCountryChange}
                className="border border-gray-300 rounded-lg px-4 py-2.5 w-full sm:w-auto focus:outline-none focus:ring focus:ring-blue-500"
              >
                <option value="">Select a country</option>
                {partnersData.map((region) => (
                  <option key={region.country} value={region.country}>
                    {`${region.country} (${region.partners.length})`}
                  </option>
                ))}
              </select>
              <div className="relative mt-4 md:mt-0">
                <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                  </svg>
                </div>
                <input type="search"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); handleSearch(e.target.value); }}
                  className="border border-gray-300 rounded-lg px-4 py-2.5 ps-10 w-full sm:w-96 focus:outline-none focus:ring focus:ring-blue-500"
                  placeholder="Search for a partner or a country..." />
              </div>
            </div>
          </div>

          {filteredData.length === 0 ? (
            <p className="text-center text-red-500">
              {selectedCountry
                ? "There are no matching partners found for the selected country."
                : "No partners found for the given search."}
            </p>
          ) : (
            filteredData.map((region) => {
              
              const premiumPartners = region.partners.filter(
                (p) => p.premium === true
              );
              const regularPartners = region.partners.filter(
                (p) => !p.premium
              );

              return (
                <div key={region.country} className="mb-12">
                  <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b border-gray-300 pb-2">
                    {region.country}
                  </h3>

                  {premiumPartners.length > 0 && (
                    <div className="mb-5">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {premiumPartners.map((partner) => (
                          <PartnerCard key={partner.name} partner={partner} />
                        ))}
                      </div>
                    </div>
                  )}

                  {regularPartners.length > 0 && (
                    <div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {regularPartners.map((partner) => (
                          <PartnerCard key={partner.name} partner={partner} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>
    </>
  );
};

export default Partners;
